import { recursiveSnakeCaseCipher } from "@src/global_functions/postgrestApi";
import { useInvalidateSiteQueries } from "@src/global_functions/sites/sitesQueries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CamelCasedPropertiesDeep } from "type-fest";
import FetchClients, { ClientOutput } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";
import { BUILDING_QUERY_KEY } from "./building";
import { PARKING_QUERY_KEYS } from "./detachedParking";
import { SURFACE_LOT_QUERY_KEY } from "./surfaceLot";
import type { BuildingSnakeCase, SnakeCaseDetachedParking } from "./util";
const bulkStructures = { mutations: { usePut } };

export default bulkStructures;

type Body = Pick<
  ClientOutput["tenantsAndSpaces"]["BulkStructuresRequest"],
  "surface_lot"
> & { parkings: SnakeCaseDetachedParking[]; buildings: BuildingSnakeCase[] };
export type CamelCaseBody = CamelCasedPropertiesDeep<Body>;

const putBulkStructures = async (site_id: number, body: CamelCaseBody) => {
  const { error, response } = await FetchClients.tenantsAndSpaces.PUT(
    "/{site_id}/structures",
    {
      body: recursiveSnakeCaseCipher(body),
      params: { path: { site_id } },
    }
  );
  if (response.ok) return;
  if (isBackpackResponseErrorBody(error)) {
    throw new BackpackServiceError(error);
  }
  throw new Error(
    typeof error === "string"
      ? error
      : "Failed to submit structures. Please contact Backpack Support if the problem persists."
  );
};

function usePut(siteId: number) {
  const { refreshSiteQueriesAsync } = useInvalidateSiteQueries({ siteId });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CamelCaseBody) => putBulkStructures(siteId, body),
    onSuccess: () =>
      Promise.all([
        refreshSiteQueriesAsync(),
        queryClient.invalidateQueries({
          queryKey: [BUILDING_QUERY_KEY, siteId],
        }),
        ...Object.values(PARKING_QUERY_KEYS).map((key) =>
          queryClient.invalidateQueries({ queryKey: [key, siteId] })
        ),
        queryClient.invalidateQueries({
          queryKey: [SURFACE_LOT_QUERY_KEY, siteId],
        }),
      ]),
  });
}
